import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { ButtonGroup, Button } from 'react-bootstrap';
import {
  CELSIUS_ID,
  FAHRENHEIT_ID, SPEED_FEET, SPEED_METERS,
} from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelValueRender';
import {
  SENSOR_DATA_SOURCE_TYPE,
  SENSOR_DATA_DEFAULT_CONTEXT,
  TIME_RANGE_OPTIONS,
  isGraphVisible,
  isTableVisible,
} from './sensorDataWidgetTypes';
import SensorDataGraph from './SensorDataGraph';
import SensorDataTable from './SensorDataTable';
import { getPublicDashboardToken } from '../../services/dashboardApi';
import styles from './SensorDataWidget.module.css';

const defaultTitleFor = (sourceType) => (
  sourceType === SENSOR_DATA_SOURCE_TYPE.METRIC_GROUP
    ? 'Sensor Data'
    : 'High & Low Temperature Trends'
);

const buildColorMap = (series) => {
  const map = {};
  series.forEach((s) => {
    if (s.channelId != null) map[s.channelId] = s.color;
  });
  return map;
};

const NeedsConfiguration = () => (
  <div className="p-4 text-center text-muted">
    <div className="mb-2">This widget is not configured.</div>
    <small>Edit the widget and select a data group to display.</small>
  </div>
);

const SensorDataWidgetView = ({
  context, data, onContextChange,
}) => {
  const {
    sourceType = SENSOR_DATA_DEFAULT_CONTEXT.sourceType,
    displayType = SENSOR_DATA_DEFAULT_CONTEXT.displayType,
    timeRange = SENSOR_DATA_DEFAULT_CONTEXT.timeRange,
    metricGroupId = null,
  } = context;

  const series = (data && data.series) || [];
  const rows = (data && data.rows) || [];
  const graphOverlays = (data && data.graphOverlays) || [];
  const samplesLoading = !!(data && data.samplesLoading);
  const samplesError = !!(data && data.samplesError);

  const showGraph = isGraphVisible(displayType);
  const showTable = isTableVisible(displayType);
  const isPublicPage = !!getPublicDashboardToken();
  const needsConfiguration = sourceType === SENSOR_DATA_SOURCE_TYPE.METRIC_GROUP
    && metricGroupId == null;

  const title = useMemo(() => {
    const groupName = data && data.metricGroupName;
    if (groupName) return groupName;
    return defaultTitleFor(sourceType);
  }, [data, sourceType]);

  const colorByChannelId = useMemo(() => buildColorMap(series), [series]);

  const rangeDays = useMemo(() => {
    const opt = TIME_RANGE_OPTIONS.find((o) => o.value === timeRange);
    return opt ? opt.days : null;
  }, [timeRange]);

  if (needsConfiguration) return <NeedsConfiguration />;

  const handleTimeRangeChange = (value) => {
    if (value === timeRange) return;
    onContextChange({ ...context, timeRange: value });
  };

  const showTimeRangeSelector = showGraph && !isPublicPage;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h6 className={styles.title}>{title}</h6>
        {showTimeRangeSelector && (
          <div className={styles.headerActions}>
            <ButtonGroup size="sm" aria-label="Time range">
              {TIME_RANGE_OPTIONS.map((opt) => {
                const isActive = opt.value === timeRange;
                return (
                  <Button
                    key={opt.value}
                    variant={isActive ? 'primary' : 'outline-secondary'}
                    active={isActive}
                    aria-pressed={isActive}
                    onClick={() => handleTimeRangeChange(opt.value)}
                  >
                    {opt.label}
                  </Button>
                );
              })}
            </ButtonGroup>
          </div>
        )}
      </div>

      {showGraph && (
        <div className={`${styles.graphCard} ${!showTable ? styles.graphOnly : ''}`}>
          <SensorDataGraph
            series={series}
            rangeDays={rangeDays}
            graphOverlays={graphOverlays}
          />
          {samplesLoading && (
            <div className={`${styles.graphOverlay} ${styles.muted}`}>
              Loading samples…
            </div>
          )}
          {samplesError && (
            <div className={`${styles.graphOverlay} ${styles.error}`}>
              Failed to load samples.
            </div>
          )}
        </div>
      )}

      {showTable && (
        <div
          className={`${styles.tableCard} ${
            showGraph ? styles.tableWithGraph : styles.tableOnly
          }`}
        >
          <div className={styles.tableScroll}>
            <SensorDataTable rows={rows} colorByChannelId={colorByChannelId} />
          </div>
        </div>
      )}
    </div>
  );
};

SensorDataWidgetView.propTypes = {
  context: PropTypes.shape({
    sourceType: PropTypes.string,
    metricGroupId: PropTypes.number,
    displayType: PropTypes.string,
    timeRange: PropTypes.string,
  }).isRequired,
  data: PropTypes.shape({
    metricGroupName: PropTypes.string,
    series: PropTypes.arrayOf(PropTypes.object),
    rows: PropTypes.arrayOf(PropTypes.object),
    samplesLoading: PropTypes.bool,
    samplesError: PropTypes.bool,
    graphOverlays: PropTypes.arrayOf(PropTypes.object),
  }),
  onContextChange: PropTypes.func.isRequired,
  userPreferences: PropTypes.shape({
    temperatureScale: PropTypes.oneOf([CELSIUS_ID, FAHRENHEIT_ID]),
    airSpeedScale: PropTypes.oneOf([SPEED_METERS, SPEED_FEET]),
  }).isRequired,
};

SensorDataWidgetView.defaultProps = {
  data: null,
};

export default SensorDataWidgetView;
